import { useEffect, useState } from "react"
import { useNavigate } from "react-router-dom"

import { App } from "@/atoms"
import { NAMES_ROUTES } from "@/const/routes.constants"
import { getDetailMessage } from "@/helpers/error.helper"
import { useOrderBy } from "@/hooks/useOrderBy"
import { useTooltip } from "@/hooks/useTooltip"
import { useTranslation } from "@/hooks/useTranslation"
import { DeleteConfirmModal, useConfirmModal } from "@/molecules/ConfirmModal"
import { LoadErrorEmptyTable } from "@/molecules/EmptyTable"
import { ExpandIconTable } from "@/molecules/ExpandIconTable"
import { AI_POLICY_VIOLATIONS_DRAWER_ID } from "@/organisms/Drawers/const"
import { useDrawer } from "@/organisms/Drawers/hooks/useDrawer"
import { AI_POLICIES_VIOLATIONS_WINDOW_DAYS } from "@/pages/GovernancePage/applications/ai-policies/useLiveAiPolicies"
import { useAiPolicies } from "@/pages/GovernancePage/applications/policies/useAiPolicies"
import { HEADING_KEY } from "@/pages/GovernancePage/components/organisms/AiPolicyProvider/getData"
import { TabTemplate } from "@/templates/TabTemplate"
import { ExpandedRowAiPoliciesTab } from "../ExpandedRowAiPoliciesTab"
import { FilterAiPoliciesTab } from "../FilterAiPoliciesTab"
import { getDemoColumnsTable, getLiveColumnsTable } from "./getColumnsTable"

import type { PolicyRecordInput } from "@/pages/GovernancePage/applications/policies/useAiPolicies.types"
import type { ActionsAiPolicyCellProps } from "@/pages/GovernancePage/components/pages/AiPoliciesPage/components/ActionsAiPolicyCell"
import type { StatusAiPolicyCellProps } from "../StatusAiPolicyCell"

const SAVING_MESSAGE_KEY = "status-ai-policies-governance-page"

export const AiPoliciesTab = () => {
  const { response, listed, isPending, features, live, findById, source } =
    useAiPolicies()
  const { t } = useTranslation()
  const { message } = App.useApp()
  const { confirm } = useConfirmModal()
  const navigate = useNavigate()
  const { onSorterChange, ...orderBy } = useOrderBy()
  const violationsDrawer = useDrawer(AI_POLICY_VIOLATIONS_DRAWER_ID)
  const dataSource = listed ?? response?.data
  // A switched status shows at once, as an SCM policy's does, until the
  // policies say it too, or it fails and turns back
  const [switched, setSwitched] = useState<Record<string, boolean>>({})
  const [isSaving, setIsSaving] = useState(false)
  const enabledByPolicy = (dataSource ?? [])
    .map((record) =>
      record.type !== "heading" && record.live
        ? `${record.id}:${record.live.enabled}`
        : ""
    )
    .join()
  const view = (id: string) =>
    navigate(
      `/${NAMES_ROUTES.GOVERNANCE.ROOT}/${NAMES_ROUTES.GOVERNANCE.AI}/${id}/view`
    )
  const getRecord = (id: string) => {
    const record = findById(id)

    return record && record.type !== "heading" ? record : undefined
  }
  // A real policy's violations' details show on hover, as a Last Scan cell's do
  useTooltip(live ? dataSource : undefined)

  const forget = (id: string) =>
    setSwitched(({ [id]: _forgotten, ...rest }) => rest)

  useEffect(() => {
    setSwitched((previous) => {
      const kept = Object.entries(previous).filter(([id, enabled]) => {
        const record = getRecord(id)

        return record?.live && record.live.enabled !== enabled
      })

      return kept.length === Object.keys(previous).length
        ? previous
        : Object.fromEntries(kept)
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabledByPolicy])

  const liveActions: ActionsAiPolicyCellProps["live"] = live && {
    t,
    canWrite: live.canWrite,
    edit: ({ id }) =>
      navigate(
        `/${NAMES_ROUTES.GOVERNANCE.ROOT}/${NAMES_ROUTES.GOVERNANCE.AI}/${id}/edit`
      ),
    remove: ({ id }) => {
      const record = getRecord(id)

      if (!record) return

      confirm((attr) =>
        DeleteConfirmModal({
          name: record.policyName,
          entityName: "ai-policy",
          onOk: () => live.remove(record),
          ...attr
        })
      )
    }
  }
  const onStatusChange = async (
    enabled: boolean,
    record: PolicyRecordInput
  ) => {
    const id = `${record.id}`

    if (!live) return

    setSwitched((previous) => ({ ...previous, [id]: enabled }))
    setIsSaving(true)
    message.loading({
      key: SAVING_MESSAGE_KEY,
      content: t("governance.policies.saving"),
      duration: 0
    })
    try {
      await live.setEnabled(record, enabled)
      message.success({
        key: SAVING_MESSAGE_KEY,
        content: t("governance.policies.saved")
      })
    } catch (error) {
      forget(id)
      message.error({
        key: SAVING_MESSAGE_KEY,
        content: getDetailMessage(error) || t("error.serverError")
      })
    } finally {
      setIsSaving(false)
    }
  }
  const status: StatusAiPolicyCellProps = {
    isEnabled: (record) => switched[`${record.id}`] ?? !!record.live?.enabled,
    onChange: onStatusChange,
    disabled: isSaving,
    noPermissions: live ? !live.canWrite : undefined
  }

  return (
    <TabTemplate
      sticky
      resizable
      columns={
        source === "live"
          ? getLiveColumnsTable(t, {
              view,
              live: liveActions,
              violationsWindowDays: AI_POLICIES_VIOLATIONS_WINDOW_DAYS,
              openViolations: (id) => violationsDrawer.open({ record: { id } }),
              status,
              sorter: features.listQuery ? orderBy : undefined
            })
          : getDemoColumnsTable(t, { view })
      }
      loading={isPending}
      dataSource={dataSource}
      SearchComponent={features.listQuery ? <FilterAiPoliciesTab /> : undefined}
      onChange={
        features.listQuery
          ? (_pagination, _filters, sorter) => {
              onSorterChange(sorter)
            }
          : undefined
      }
      locale={
        live && {
          emptyText: live.isError ? (
            <LoadErrorEmptyTable onRetry={live.refetch} />
          ) : (
            t("governance.ai.live.empty")
          )
        }
      }
      onRow={(record) => ({
        onClick: (e) => {
          e.preventDefault()
          const target = e.target as HTMLElement
          const className = ".ant-table-row-expand-icon-cell"
          const isExpand =
            target.classList.contains(className) || target.closest(className)
          const { type } = record
          const isViewExist = type === "scope" || type === "agent"

          if (!isExpand && isViewExist) view(record.id)
        }
      })}
      expandable={{
        expandIcon: (props) => {
          const { key, type } = props.record

          if (!features.agentRows) return null
          if (type === "agent") return null
          if (type === "heading" || key.startsWith(HEADING_KEY)) return null

          return <ExpandIconTable {...props} />
        },
        rowExpandable: ({ expandable }) => expandable,
        expandedRowRender: (props) => <ExpandedRowAiPoliciesTab {...props} />
      }}
    />
  )
}
